import React from "react";
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Button,
  Box,
  Chip,
  Alert,
  useTheme,
} from "@mui/material";
import {
  WarningAmber as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../config/routes";

interface ActionItem {
  id: string;
  type: "notice" | "withdrawal" | "request";
  title: string;
  description?: string;
  date: string;
  status?: string;
  linkTo: string;
}

interface DashboardActionItemsProps {
  unacknowledgedNotices: Array<{
    assignmentId: number;
    title: string;
    noticeType: string;
    assignedDate: string;
  }>;
  pendingWithdrawals: Array<{
    purposeId: number;
    purposeName: string;
    requestId: number;
    status: string;
    estimatedCompletionDate?: string;
  }>;
  urgentRequests: Array<{
    requestId: number;
    requestType: string;
    status: string;
    createdDate: string;
  }>;
  loading?: boolean;
}

const DashboardActionItems: React.FC<DashboardActionItemsProps> = ({
  unacknowledgedNotices,
  pendingWithdrawals,
  urgentRequests,
  loading = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const actionItems: ActionItem[] = [
    ...unacknowledgedNotices.slice(0, 5).map((notice) => ({
      id: `notice-${notice.assignmentId}`,
      type: "notice" as const,
      title: notice.title,
      description: `${notice.noticeType} notice`,
      date: notice.assignedDate,
      linkTo: ROUTES.NOTICES,
    })),
    ...pendingWithdrawals.slice(0, 5).map((withdrawal) => ({
      id: `withdrawal-${withdrawal.requestId}`,
      type: "withdrawal" as const,
      title: withdrawal.purposeName,
      description: `Withdrawal request #${withdrawal.requestId}`,
      date: withdrawal.estimatedCompletionDate || "",
      status: withdrawal.status,
      linkTo: `/purposes/${withdrawal.purposeId}`,
    })),
    ...urgentRequests.slice(0, 5).map((request) => ({
      id: `request-${request.requestId}`,
      type: "request" as const,
      title: `${request.requestType} Request #${request.requestId}`,
      description: `Status: ${request.status}`,
      date: request.createdDate,
      status: request.status,
      linkTo: `/requests/${request.requestId}`,
    })),
  ].slice(0, 5); // Limit to 5 total items

  if (loading) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Action Items
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (actionItems.length === 0) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Action Items
            </Typography>
          </Box>
          <Alert severity="success" sx={{ mt: 2 }}>
            No action items at this time. You're all caught up!
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const getItemIcon = (type: ActionItem["type"]) => {
    switch (type) {
      case "notice":
        return <WarningIcon color="warning" fontSize="small" />;
      case "withdrawal":
        return <AccessTimeIcon color="info" fontSize="small" />;
      case "request":
        return <WarningIcon color="error" fontSize="small" />;
    }
  };

  const getItemColor = (type: ActionItem["type"]) => {
    switch (type) {
      case "notice":
        return theme.palette.warning.main;
      case "withdrawal":
        return theme.palette.info.main;
      case "request":
        return theme.palette.error.main;
    }
  };

  return (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Action Items
          </Typography>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={() => {
              // Navigate to the most relevant page
              if (unacknowledgedNotices.length > 0) {
                navigate(ROUTES.NOTICES);
              } else if (pendingWithdrawals.length > 0) {
                navigate(ROUTES.PURPOSES);
              } else {
                navigate(ROUTES.REQUESTS);
              }
            }}
          >
            View All
          </Button>
        </Box>
        <List>
          {actionItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => navigate(item.linkTo)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Box sx={{ color: getItemColor(item.type), mr: 1.5 }}>
                    {getItemIcon(item.type)}
                  </Box>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        {item.description && (
                          <Typography variant="caption" color="text.secondary">
                            {item.description}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          {formatDate(item.date)}
                        </Typography>
                      </Box>
                    }
                  />
                  {item.status && (
                    <Chip
                      label={item.status}
                      size="small"
                      sx={{
                        ml: 1,
                        height: 20,
                        fontSize: "0.65rem",
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
              {index < actionItems.length - 1 && <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mx: 2 }} />}
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default DashboardActionItems;

